'use client'

import { css, cx } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { Dispatch, SetStateAction, useCallback, useEffect } from 'react'

import { LoaderWrapperDS } from '@src/components/blocks/loader-wrapper-ds'
import { SearchResultsDS } from '@src/components/blocks/search-results-ds'
import { SelectedFilter } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import { useSearchResults } from '@src/pages/search/searchResultsSection/hooks/useSearchResults'
import {
  FilterSectionWithDrawer,
  FilterSectionWithDrawerProps,
  MobileFilters,
  UpdateQueryParams,
} from '@src/quince-components/src/atoms/filterSection/menu'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useRecurringItemSubscriptionsExperiment from '@src/quince-site-common/src/hooks/useRecurringItemSubscriptionsExperiment'
import SomethingWentWrong from '@src/quince-site-common/src/organisms/somethingWentWrong/somethingWentWrong'

/* eslint-disable i18next/no-literal-string -- Panda design tokens and raw lengths, not user-facing copy */

// Legacy `.newCLPFiltersSearchTitleContainer` — desktop mb 2.4rem, mobile mb 1.4rem.
const titleContainerClass = css({
  mb: { base: '1.4rem', lg: 'space-24' },
})

// Legacy `.newCLPFiltersSearchTitle` — desktop heading; mobile shrinks to a 1.4rem centered line.
// `padding 0 4.4rem` and `letter-spacing 0.1rem` have no Panda tokens at this scale; raw lengths.
const titleClass = css({
  fontSize: { base: '1.4rem', lg: '2rem' },
  fontWeight: 500,
  lineHeight: { base: '1.8rem', lg: '2.4rem' },
  letterSpacing: '0.1rem',
  px: '4.4rem',
  mt: { base: 'space-24', lg: 'space-32' },
  color: 'black',
  textAlign: { base: 'center', lg: 'left' },
})

// Legacy `.newCLPFiltersProductCountText` — `dark-gray-2` maps to DS `darkgray`.
const countClass = css({
  mt: 'space-8',
  fontSize: '1.1rem',
  lineHeight: '1.4rem',
  fontWeight: 400,
  color: { base: 'black', lg: 'darkgray' },
  px: { base: '0', lg: '4.4rem' },
  textAlign: { base: 'center', lg: 'left' },
})

// Legacy `.searchResultsFilterSection` — mb 3.2rem.
const filterSectionClass = css({
  mb: 'space-32',
})

// Legacy `.searchResultsFilterSection__withHiddenHeader` — desktop adds pt 3.2rem; mobile 0.
const filterSectionHiddenHeaderClass = css({
  pt: { base: '0', lg: 'space-32' },
})

// Legacy `.somethingWentWrongWrapper`.
const somethingWentWrongClass = css({
  mt: '15rem',
})

/* eslint-enable i18next/no-literal-string */

interface SearchResultsSectionDSProps {
  setSearchLoaded: Dispatch<SetStateAction<boolean>>
}

export function SearchResultsSectionDS({ setSearchLoaded }: SearchResultsSectionDSProps) {
  const isRecurringItemSubscriptionsExpEnabled = useRecurringItemSubscriptionsExperiment()

  const {
    productCards,
    isLayoutLoading,
    isError,
    searchQuery,
    intersectRef,
    isMoreProductsLoading,
    totalProducts,
    filters,
    selectedFilters,
    updateQuery,
    hideSearchHeader,
  } = useSearchResults(isRecurringItemSubscriptionsExpEnabled)

  useEffect(() => {
    setSearchLoaded(!isLayoutLoading)
  }, [isLayoutLoading, setSearchLoaded])

  const { t } = useTranslation('common')
  const { deviceType } = useDeviceInfo()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP

  const hasAppliedFilters = SelectedFilter.isAnyFilterSelected(selectedFilters)
  const shouldRenderFilters = totalProducts > 0 || hasAppliedFilters

  const handleResetFilters = useCallback(() => {
    updateQuery({ filter: { type: 'reset' } })
  }, [updateQuery])

  if (isError) {
    return (
      <SomethingWentWrong
        wrapperClass={somethingWentWrongClass}
        // eslint-disable-next-line i18next/no-literal-string -- CSS length value, not user-facing copy
        minHeight={'20rem'}
      />
    )
  }

  // TODO: migrate `FilterSectionWithDrawer` / `MobileFilters` to DS so these
  // transient legacy-atom imports can be dropped. Sidebar variant is intentionally
  // out of scope for the DS path.
  const FilterComponent: React.ComponentType<FilterSectionWithDrawerProps> = isDesktop
    ? FilterSectionWithDrawer
    : (MobileFilters as React.ComponentType<FilterSectionWithDrawerProps>)

  const renderFilterSection = filters.length > 0 && shouldRenderFilters

  return (
    <div>
      {/* eslint-disable-next-line i18next/no-literal-string -- CSS length value */}
      <LoaderWrapperDS showLoader={isLayoutLoading} minHeight={'26rem'}>
        {searchQuery && !hideSearchHeader && totalProducts > 0 ? (
          <div className={titleContainerClass}>
            <h1 className={titleClass}>{`"${searchQuery.toUpperCase()}"`}</h1>
            <p className={countClass}>
              {`${t('showing')} ${t(totalProducts === 1 ? 'totalitems_item' : 'totalitems_items', {
                totalItems: totalProducts,
              })}`}
            </p>
          </div>
        ) : null}

        {/* eslint-disable-next-line i18next/no-literal-string -- DOM id used by search-page tests / observers */}
        <div id={'newSearchSection'}>
          {/* Filter renders as the first child INSIDE #newSearchSection to match the legacy
              default (non-experiment) path (search-results-section-legacy.tsx: the
              `!showDesktopFilterWithDrawer` branch nests the filter inside #newSearchSection).
              This keeps E2E selectors (`#newSearchSection [data-testid=...]`), scroll/focus
              anchors and analytics scoped to #newSearchSection consistent with legacy. */}
          {renderFilterSection ? (
            <FilterComponent
              noOfItems={totalProducts}
              filters={filters}
              // eslint-disable-next-line i18next/no-literal-string -- sortBy key, not user-facing copy
              sortBy={''}
              selectedFilters={selectedFilters as unknown as Record<string, string[]>}
              updateQuery={updateQuery as (params: UpdateQueryParams) => void}
              showSelectedOption
              shouldRenderColorFilterHtml={false}
              skipSorting
              className={cx(filterSectionClass, (hideSearchHeader || !totalProducts) && filterSectionHiddenHeaderClass)}
            />
          ) : null}

          <SearchResultsDS
            searchQuery={searchQuery}
            productCards={productCards}
            intersectRef={intersectRef}
            isMoreProductsLoading={isMoreProductsLoading}
            onResetFilters={handleResetFilters}
            selectedFilters={selectedFilters}
          />
        </div>
      </LoaderWrapperDS>
    </div>
  )
}
